import React from 'react';
import { Alert } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useOffline } from './OfflineProvider';

/**
 * Shared "needs an internet connection" notice for sections that can never
 * work offline — external integrations (Stripe / QuickBooks / Keystone),
 * subscription management, team management, uploads, AI surfaces.
 *
 * Reactive: driven by useOffline().isOnline (the observed-reachability state),
 * so it appears the moment connectivity is observed down and clears live on
 * reconnect. Renders NOTHING while online, and nothing flag-off (without an
 * OfflineProvider the context default is isOnline: true).
 */
export const OfflineNotice: React.FC<{ children: React.ReactNode; sx?: object }> = ({
  children,
  sx,
}) => {
  const { isOnline } = useOffline();
  if (isOnline) return null;
  return (
    <Alert severity="info" icon={<CloudOffIcon fontSize="inherit" />} sx={{ mb: 2, ...sx }}>
      {children}
    </Alert>
  );
};

/**
 * Reactive offline flag for disabling buttons/inputs alongside an
 * OfflineNotice. False whenever the flag is off or the provider is absent.
 */
export const useIsOffline = (): boolean => !useOffline().isOnline;
